// Which ending case the game bar shows for the open game, from what the
// review knows (did the game reach an ending, is it scanned) and the store's
// facts for it. Pure, so it can be tested without a review or a store.
import type { EndingCase, GameFacts, StoreStatus } from '@chess/api-model';
import type { ReviewState } from '@chess/api-review';

export interface EndingInputs {
  /** The review's phases put an ending in the game. */
  reached: boolean;
  /** The open game has a scan. */
  scanned: boolean;
  /** The store's status; undefined before its provider has registered. */
  status?: StoreStatus;
  /** The game as the store holds it: absent when the store has no such game (older than the period). */
  stored?: { facts?: Pick<GameFacts, 'endingCase'> };
}

/**
 * The case to show, `'loading'` while the store is still reading (so the bar
 * reserves the chip's width), or undefined: no ending, no scan, a store that
 * failed, a game outside the period, or facts without a case.
 */
export function endingOf({ reached, scanned, status, stored }: EndingInputs): EndingCase | 'loading' | undefined {
  if (!reached || !scanned) return undefined;
  if (status === undefined || status === 'loading') return 'loading';
  if (status !== 'ready') return undefined;
  return stored?.facts?.endingCase;
}

/**
 * Where the chip's click takes the board: the ending's first position, where
 * a click on the Ending tab lands (`Stages.entryPly`), so the Ending tab is
 * the one shown; the game's final position when it never reached an ending.
 * Undefined without a game.
 */
export function endingEntryPly(state: Pick<ReviewState, 'stages'>): number | undefined {
  return state.stages?.entryPly('ending');
}
